<template>
  <div class="farm-window research-card">
    <img :src="research.image"/>
    <div class="info-panel">
      <h3>{{ research.name }}</h3>
      <div class="option-buttons">
        <p><b>Target:</b></p> <p :title="targetTooltip()">{{ research.displayTarget }}</p>
        <p><b>Effect:</b></p><p>
          <span :title="permanenceTooltip()">{{ research.displayPermanence }}, </span>
          <span :title="durationTooltip()">{{ research.displayDuration }}</span>
        </p>
        <p><b>Costs:</b></p><p :title="durationTooltip()">{{ research.displayCosts }}</p>
      </div>
      <p style="white-space: pre-wrap;">{{ research.description }}</p>
    </div>
    <div class="status-panel">
      <template v-if="farm.state.money >= farm.state.lab.getResearchCost(research) || research.isStarted">
        <TextButton @click="farm.research(farm.state.lab.researchById(research.id))"
                    class="farm-button"
                    :class="{'disabled-button': research.isCompleted || research.isBeingResearched}">
          <p v-if="!research.isStarted">Start Research ${{ lab.getResearchCost(research).toFixed(0) }}</p>
          <p v-else-if="!research.isBeingResearched">Continue Research</p>
          <p v-else-if="research.isCompleted">Completed</p>
          <p v-else>Researching...</p>
        </TextButton>
      </template>
      <template v-else>
        <TextButton @click="farm.research(farm.state.lab.researchById(research.id))"
                    class="farm-button disabled-button">
          <p>Can't afford research project (${{ lab.getResearchCost(research).toFixed(0) }})</p>
        </TextButton>
      </template>
      <ValueMeter :container="research.completion">
        <p class="meter-label">{{ research.completion.value.toFixed(0) }} / {{ research.completion.max.toFixed(0) }} Science</p>
      </ValueMeter>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import Research from '@/Research'
import ValueMeter from '@/components/ui/ValueMeter.vue'
import TextButton from '@/components/ui/TextButton.vue'
import Farm from '@/farm/Farm'

@Options({
  components: {
    ValueMeter, TextButton
  },
  props: {
    research: Research,
    farm: Object
  },
  methods: {
    log(msg: any) {
      console.log(msg)
    },
    targetTooltip() {
      if (this.research.effectType.target === 'farm') {
        return 'When researched, this will produce an upgrade for the farm.'
      } else if (this.research.effectType.target === 'employee') {
        return 'When researched, this will produce an item or effect for your employees.'
      }
      return ''
    },
    permanenceTooltip() {
      if (this.research.effectType.permanence === 'permanent') {
        return 'If use of the research is halted, effects already incurred are permanent.'
      } else if (this.research.effectType.permanence === 'temporary') {
        return 'If use of the research is halted, all effects will dissipate over time.'
      }
      return ''
    },
    durationTooltip() {
      if (this.research.effectType.timeType === 'instant') {
        return 'When used, all effects of this research will be applied instantly.'
      } else if (this.research.effectType.timeType === 'slow') {
        return 'When used, effects of this research will take some time before reaching full intensity.'
      }
      return ''
    }
  },
  computed: {
    lab() {
      return this.farm.state.lab
    }
  }
})
export default class ResearchCard extends Vue {
  isLoaded = false
  research!: Research
  farm!: Farm
}
</script>

<style scoped lang="scss">
  .research-card {
    display: grid;
    grid-template-rows: 1fr 100px;
    grid-template-columns: 3fr 4fr;
    grid-template-areas: "a b"
                         "a c";
    min-height: 300px;
    max-height: 500px;
    //max-width: $main-column-max-width;
    overflow: hidden;
    margin-left: auto;
    margin-right: auto;
    padding: 0;

    @media only screen and (max-width: $mobile-width){
      grid-template-rows: auto minmax(200px, auto) 100px;
      grid-template-columns: 1fr;
      grid-template-areas: "a" "b" "c";
      height: auto;
      max-height: none;
    }
  }

  img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: 50% 50%;
    pointer-events: none;
    user-select: none;
    grid-area: a;

    background: $research-thumb-background;

    @media only screen and (max-width: $mobile-width) {
      height: 200px;
      object-fit: contain;
    }
  }

  .info-panel {
    grid-area: b;
    padding: 0.5em;
    text-align: left;
    border-left: inherit;

    h3 {
      margin-top: 0;
      margin-bottom: 0.5em;
    }
  }

  .status-panel {
    display: grid;
    grid-template-rows: 1fr 1fr;
    border-left: inherit;
    grid-area: c;
  }

  .text-button {
    text-align: center;
    border-top: $component-border-style;
    height: 50px;

    display: table;

    p {
      display: table-cell;
      vertical-align: middle;
    }
  }

  .disabled-button {
    background-color: $component-button-disabled;
    cursor: default;
    pointer-events: none;
  }

  .value-meter {
    height: 100%;
    border: 0;
    border-top: $component-border-style;
  }

  .meter-label {
    margin: 4px 0 0;
  }

  .option-buttons {
    display: grid;
    grid-template-columns: auto 3fr;
    //grid-auto-flow: column;

    p {
      text-align: left;
      margin-top: 4px;
      margin-bottom: 4px;
      margin-right: 1ch;
    }

    .text-button {
      display: table;
      border: 1px solid black;
      border-right: 0;
      margin-top: auto;
      margin-bottom: auto;
      height: 80%;

      &:last-child {
        border: 1px solid black;
      }

      p {
        display: table-cell;
        vertical-align: middle;
      }
    }
  }

</style>
